import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../ui/card";
import { Zap, ShieldCheck, BrainCircuit, Blocks, Globe, Cpu, ArrowRight } from "lucide-react";
import Navbar from "./Navbar3";
import AgenticEraSection from './AgenticEraSection'
import Footer from "../layout/Footer";

import { Bot, Headphones, Code2, LineChart } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function AIAgents() {
  const agents = [
    {
      name: 'Voice Agents',
      description: 'Human-like conversational AI for customer support, inbound sales, and appointment scheduling.',
      icon: <Headphones className="w-6 h-6 text-purple-400" />
    },
    {
      name: 'Data Agents',
      description: 'Autonomous agents that analyze, visualize, and extract real-time insights from complex datasets.',
      icon: <LineChart className="w-6 h-6 text-blue-400" />
    },
    {
      name: 'Coding Agents',
      description: 'Accelerate development with specialized AI that writes, reviews, and debugs code alongside your team.',
      icon: <Code2 className="w-6 h-6 text-teal-400" />
    },
    {
      name: 'Support Agents',
      description: 'Intelligent ticketing and resolution systems operating 24/7 to deliver seamless user experiences.',
      icon: <Bot className="w-6 h-6 text-indigo-400" />
    }
  ];

  return (
    <section className="py-24 relative z-10">
      <div className="text-center mb-14">
        <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white tracking-tight">
          Specialized <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">AI Agents</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto text-lg">
          Deploy role-specific AI agents that seamlessly integrate into your workflows, making autonomous decisions and executing complex tasks at scale.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {agents.map((agent, index) => (
          <div 
            key={index}
            className="gsap-agent-card p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.07] hover:border-blue-500/40 transition-all duration-500 backdrop-blur-md group shadow-2xl shadow-black/20"
          >
            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-black/20">
              {agent.icon}
            </div>
            <h3 className="text-xl font-semibold text-gray-100 mb-3">{agent.name}</h3>
            <p className="text-gray-400 leading-relaxed text-sm">
              {agent.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <Card
      className="gsap-feature-card bg-white/[0.03] border border-white/10 text-gray-100
      hover:border-blue-500/40 hover:shadow-2xl hover:shadow-blue-900/20
      hover:-translate-y-1 transition-all transform duration-500 ease-out
      backdrop-blur-xl rounded-2xl overflow-hidden"
    >
      <CardHeader className="flex flex-col items-center gap-4 pt-8">
        <div
          className="flex items-center justify-center w-16 h-16 rounded-2xl
          bg-blue-600/10 border border-blue-500/30 backdrop-blur-md"
        >
          {icon}
        </div>
        <CardTitle className="text-xl text-center font-semibold tracking-tight">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pb-8">
        <CardDescription className="text-gray-400 text-center leading-relaxed">
          {description}
        </CardDescription>
      </CardContent>
    </Card>
  );
}

function CTA({ onBookNow, bookNowRef }) {
  return (
    <section
      id="book-now-section"
      className="relative py-28 px-4 bg-white/[0.02] rounded-3xl border border-white/10 shadow-2xl backdrop-blur-2xl 
               flex flex-col items-center gap-6 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={i}
            className="cta-particle absolute bg-white/[0.04] rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${Math.random() * 8 + 6}s`,
            }}
          />
        ))}
      </div>
      
      {/* Ambient glow behind CTA */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="relative z-10 flex flex-col items-center gap-5 text-center">
        <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight">
          Ready to Dive In?
        </h2>
        <p className="max-w-xl text-lg font-medium text-gray-400">
          Your innovation journey starts here. Partner with us and watch your vision transform into a tangible reality.
        </p>
        <button
          ref={bookNowRef}
          onClick={onBookNow}
          className="magnetic-btn group relative inline-flex items-center justify-center gap-2 font-semibold text-white px-10 py-4 mt-4
                     rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ease-out
                     bg-gradient-to-r from-blue-600 to-purple-600
                     hover:from-blue-500 hover:to-purple-500 hover:shadow-blue-500/25 hover:scale-105"
        >
          <span className="relative z-10">Book Now</span>
          <ArrowRight
            size={18}
            className="relative z-10 transform transition-transform duration-300 group-hover:translate-x-1"
          />
        </button>
      </div>
      <style jsx>{`
        .cta-particle { animation: float-up 10s ease-in-out infinite; }
        @keyframes float-up {
          0% { transform: translateY(100vh) scale(0.5); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(-10vh) scale(1); opacity: 0; }
        }
      `}</style>
    </section>
  );
}

function PartnersCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  const partners = [
    {
      description: "India's leading GPU infrastructure provider.",
      logo: "/e2e.png",
    },
    {
      description: "Our cloud platform partners.",
      logo: "/aws.png",
    },
    {
      description: "Our Database partners.",
      logo: "/mongo.png",
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === partners.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    intervalRef.current = setInterval(nextSlide, 3000);
    return () => clearInterval(intervalRef.current);
  }, );

  return (
    <section className="gsap-partners w-full py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white tracking-tight">
            Our Technology Partners
          </h2>
          <div className="mt-4 mx-auto w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
        </div>
        <div className="relative max-w-xl mx-auto mb-10">
          <div className="overflow-hidden rounded-2xl bg-white/[0.02] border border-white/10 backdrop-blur-xl p-8 shadow-2xl shadow-black/30">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {partners.map((partner, index) => (
                <div
                  key={index}
                  className="min-w-full flex-shrink-0 flex flex-col items-center justify-center p-4"
                >
                  <div className="mb-6 h-24 flex items-center">
                    <img
                      src={partner.logo}
                      alt={`Partner logo`}
                      className="object-contain max-h-full max-w-xs rounded-lg bg-white/5 p-4 border border-white/5"
                    />
                  </div>
                  <div className="text-center">
                    <p className="mt-2 text-lg text-gray-400">
                      {partner.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex justify-center gap-3">
            {partners.map((_, index) => (
              <button
                key={index}
                className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-blue-500 w-8"
                    : "bg-white/20 hover:bg-white/40"
                }`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Animated Section Divider Component
function SectionDivider() {
  return (
    <div className="relative py-12 flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
      </div>
      <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.5)] animate-pulse" />
    </div>
  );
}

// Floating ambient particles for the entire page background
function AmbientParticles() {
  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="ambient-particle absolute rounded-full bg-white/[0.03]"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${Math.random() * 4 + 1}px`,
            height: `${Math.random() * 4 + 1}px`,
            animationDelay: `${Math.random() * 15}s`,
            animationDuration: `${Math.random() * 20 + 15}s`,
          }}
        />
      ))}
      <style jsx>{`
        .ambient-particle {
          animation: ambient-float linear infinite;
        }
        @keyframes ambient-float {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) translateX(${Math.random() > 0.5 ? '' : '-'}20px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function BeamInspiredPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const bookNowButtonRef = useRef(null);

  const heroRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroSubRef = useRef(null);
  const agentsRef = useRef(null);
  const featuresRef = useRef(null);
  const partnersRef = useRef(null);
  const ctaRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);
  const sectionGlow1 = useRef(null);
  const sectionGlow2 = useRef(null);
  const sectionGlow3 = useRef(null);

  useEffect(() => {
    if (location.state?.scrollToId) {
      const targetElement = document.getElementById(location.state.scrollToId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

        if (bookNowButtonRef.current) {
          bookNowButtonRef.current.classList.add("highlight-animation");
          setTimeout(() => {
            bookNowButtonRef.current.classList.remove("highlight-animation");
          }, 1000);
        }
      }
      navigate(location.pathname, { replace: true, state: {} });
    }

    const ctx = gsap.context(() => {
      // Hero entrance
      const heroTl = gsap.timeline({ defaults: { ease: "power4.out" } });
      heroTl
        .to(heroTitleRef.current, { y: 0, opacity: 1, duration: 1.2, delay: 0.2 })
        .to(heroSubRef.current, { y: 0, opacity: 1, duration: 1 }, "-=0.8");

      // AI Agents 3D staggered reveal
      gsap.fromTo(
        ".gsap-agent-card",
        { y: 80, opacity: 0, rotateX: 15, transformPerspective: 1000 },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          duration: 0.9,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: agentsRef.current,
            start: "top 85%",
          },
        }
      );

      // Feature cards reveal
      gsap.fromTo(
        ".gsap-feature-card",
        { y: 60, opacity: 0, scale: 0.9 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: featuresRef.current,
            start: "top 85%",
          },
        }
      );

      // Partners reveal
      gsap.fromTo(
        ".gsap-partners",
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: partnersRef.current,
            start: "top 90%",
          },
        }
      );

      // CTA reveal
      gsap.fromTo(
        ctaRef.current,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 85%",
          },
        }
      );

      // Section glow reveals on scroll
      [sectionGlow1, sectionGlow2, sectionGlow3].forEach((ref) => {
        if (ref.current) {
          gsap.fromTo(
            ref.current,
            { opacity: 0, scale: 0.8 },
            {
              opacity: 1,
              scale: 1,
              duration: 1.5,
              ease: "power2.out",
              scrollTrigger: {
                trigger: ref.current,
                start: "top 90%",
              },
            }
          );
        }
      });
    });

    // Mouse parallax for background blobs
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;

      if (blob1Ref.current) {
        blob1Ref.current.style.transform = `translate(${x * 30}px, ${y * 30}px)`;
      }
      if (blob2Ref.current) {
        blob2Ref.current.style.transform = `translate(${x * -40}px, ${y * 20}px)`;
      }
      if (blob3Ref.current) {
        blob3Ref.current.style.transform = `translate(${x * 20}px, ${y * -30}px)`;
      }
    };

    // Magnetic button effect
    const handleMagneticMove = (e) => {
      const btn = document.querySelector(".magnetic-btn");
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      gsap.to(btn, {
        x: x * 0.3,
        y: y * 0.3,
        duration: 0.3,
        ease: "power2.out",
      });
    };

    const handleMagneticLeave = () => {
      const btn = document.querySelector(".magnetic-btn");
      if (!btn) return;
      gsap.to(btn, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: "elastic.out(1, 0.3)",
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    const magneticBtn = document.querySelector(".magnetic-btn");
    if (magneticBtn) {
      magneticBtn.addEventListener("mousemove", handleMagneticMove);
      magneticBtn.addEventListener("mouseleave", handleMagneticLeave);
    }

    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
      if (magneticBtn) {
        magneticBtn.removeEventListener("mousemove", handleMagneticMove);
        magneticBtn.removeEventListener("mouseleave", handleMagneticLeave);
      }
    };
  }, [location, navigate]);

  return (
    <div className="relative min-h-screen flex flex-col bg-slate-950 text-gray-100 overflow-hidden selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      {/* Noise texture overlay for premium film grain feel */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[2] opacity-30" />

      {/* Ambient floating particles */}
      <AmbientParticles />

      {/* Background Glow Blobs with mouse parallax */}
      <div
        ref={blob1Ref}
        className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 transition-transform duration-100 ease-out"
      />
      <div
        ref={blob2Ref}
        className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 transition-transform duration-100 ease-out"
      />
      <div
        ref={blob3Ref}
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 transition-transform duration-100 ease-out"
      />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-500/5 rounded-full blur-[150px] pointer-events-none z-0" />

      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 relative z-10">
        {/* --- HERO SECTION --- */}
        <section ref={heroRef} className="flex flex-col items-center text-center pt-24 pb-16 gap-8 min-h-[85vh] justify-center relative">
     
          
       
          
          <h1 
            ref={heroTitleRef}
            className="font-inter text-5xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight opacity-0 translate-y-10"
          >
            <span className="text-gradient-mask block">Build Amazing Things</span>
            <span className="font-playfair italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 block mt-2">
              Faster Than Ever
            </span>
          </h1>
          
          <p 
            ref={heroSubRef}
            className="max-w-2xl text-lg md:text-xl text-gray-400 font-light leading-relaxed opacity-0 translate-y-8"
          >
            We architect <span className="font-playfair italic text-white/70">intelligent systems</span> that help enterprises automate workflows, make smarter decisions, and deliver human-like experiences — fast, secure, and built to scale.
          </p>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-600 text-xs tracking-widest uppercase">
           
            <div className="w-px h-8 bg-gradient-to-b from-gray-500 to-transparent animate-pulse" />
          </div>
        </section>

        <SectionDivider />
        <AgenticEraSection/>

        {/* --- AI AGENTS SECTION --- */}
        <div ref={agentsRef} className="relative">
          <div 
            ref={sectionGlow1}
            className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none opacity-0"
          />
          <AIAgents />
        </div>

        <SectionDivider />

        {/* --- CORE FEATURES SECTION --- */}
        <section ref={featuresRef} className="py-24 relative">
          <div 
            ref={sectionGlow2}
            className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[130px] pointer-events-none -translate-y-1/2 opacity-0"
          />
          <h2 className="text-3xl md:text-5xl font-bold mb-14 text-center text-gradient-mask tracking-tight">
            Core Features
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            <FeatureCard
              icon={<Zap className="h-8 w-8 text-blue-400" />}
              title="Lightning Fast"
              description="Experience unparalleled speed and performance for your applications."
            />
            <FeatureCard
              icon={<ShieldCheck className="h-8 w-8 text-blue-400" />}
              title="Enterprise-Grade Security "
              description="ISO 27001 certified and SOC 2 Type II compliant, ensuring enterprise-grade."
            />
            <FeatureCard
              icon={<BrainCircuit className="h-8 w-8 text-blue-400" />}
              title="Agentic AI"
              description={"MNR's Fine-Tuned Agentic AI Approach for All Business Needs — \"Intelligence behind the System\""}
            />
            <FeatureCard
              icon={<Blocks className="h-8 w-8 text-blue-400" />}
              title="AI-Models"
              description={"Our platform integrates the industry's most trusted and high-performing AI models with security and governance first approach"}
            />
            <FeatureCard
              icon={<Globe className="h-8 w-8 text-blue-400" />}
              title="AI-Infra"
              description={"Our robust product catalogue includes tailored AI Chatbots , AI Voice agents, AI Eco system for Education, Health care, Biotechnology"}
            />
            <FeatureCard
              icon={<Cpu className="h-8 w-8 text-blue-400" />}
              title="Nvidia Blackwell"
              description={"Unique feature of MNRs Pure Ai, all our platform runs on Nvidia Blackwell 200 GPUs which is most advanced super computer"}
            />
          </div>
        </section>

        <SectionDivider />

        {/* --- PARTNERS CAROUSEL --- */}
        <div ref={partnersRef} className="relative">
          <div 
            ref={sectionGlow3}
            className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none -translate-y-1/2 opacity-0"
          />
          <PartnersCarousel />
        </div>

        <SectionDivider />

        {/* --- CTA SECTION --- */}
        <div ref={ctaRef} className="pb-24 relative">
          <CTA
            onBookNow={() => navigate("/book")}
            bookNowRef={bookNowButtonRef}
          />
        </div>
      </main>
      
      <Footer />
    </div>
  );
}